import {
  h, ref, reactive,
} from "vue";
import {
  ElMessage,
} from "element-plus";
import AbstractDialogTitle from "~/components/abstract-dialog/title.vue";
import DialogBody from "~/services/contact-support-dialog/body.vue";
import DialogFooter from "~/services/contact-support-dialog/footer.vue";
import {
  useDialogStoreV2,
} from "~/components/abstract-dialog/useDialogStoreV2";
import {
  useApiFetch, getApiErrorMessage,
} from "~/composables/useApiFetch";
import defer from "~/utils/defer";

export function useContactSupportDialog(onSubmitted?: () => void) {
  const dialogStoreV2 = useDialogStoreV2();
  const nuxtApp = useNuxtApp();
  const {$t, $i18n} = nuxtApp;
  const apiFetch = useApiFetch();
  const resolution = defer<boolean>();

  const bodyRef = ref<InstanceType<typeof DialogBody> | null>(null);
  const loading = ref(false);

  const form = reactive({
    subject: "",
    description: "",
  });

  const handleConfirm = async () => {
    if (bodyRef.value) {
      const valid = await bodyRef.value.validate();
      if (!valid) {
        return;
      }
    }

    loading.value = true;
    try {
      await apiFetch("/support-requests", {
        method: "POST",
        body: {
          subject: form.subject,
          description: form.description,
          lang: $i18n?.locale?.value || "de",
        },
      });

      ElMessage.success($t("SUPPORT.MSG_SUBMIT_SUCCESS"));
      if (onSubmitted) {
        onSubmitted();
      }
      resolution.resolve(true);
      hideDialog();
    } catch (error) {
      ElMessage.error(getApiErrorMessage(error, $t("SUPPORT.MSG_SUBMIT_FAILED")));
    } finally {
      loading.value = false;
    }
  };

  const {hideDialog} = dialogStoreV2.showDialog({
    titleComponent: h(AbstractDialogTitle, {
      title: $t("SUPPORT.DIALOG_TITLE"),
    }),
    bodyComponent: () =>
      h(DialogBody, {
        "ref": bodyRef,
        "form": form,
        "onUpdate:form": (val: {subject: string; description: string}) => {
          Object.assign(form, val);
        },
        "onSubmit": handleConfirm,
      }),
    footerComponent: () =>
      h(DialogFooter, {
        loading: loading.value,
        onConfirm: handleConfirm,
        onCancel: () => {
          resolution.resolve(false);
          hideDialog();
        },
      }),
    options: {
      dialogClass: "!w-[95%] !max-w-[540px]",
      dialogMinWidth: "",
      dialogMinHeight: "",
      allowCloseByEscape: true,
      onClose: () => {
        resolution.resolve(false);
      },
    },
  });

  return resolution.promise!;
}
